// The payout tracker (1.5.2, founder pick 2026-10-22): where a cash-out is
// between the Withdraw tap and the money landing, read from the dates the
// server keeps on each payout (GET /users/:slug/payouts: releaseAt — Stripe's
// own release date; arrivalAt — Stripe's bank date once the payout exists,
// the estimate before). No date is ever invented here: a stop without one
// simply shows no date.
import type { PayoutBatch } from "../types/payouts";
import { formatLocale, type MessageKey } from "../i18n";

// Every status a cash-out passes through before it's paid (server
// models/Payout.js payoutStatus). failed / canceled / paid are not moving.
const MOVING = new Set(["awaiting_funds", "preparing", "pending", "in_transit"]);

export type TrackerKind = "bank" | "card" | "wallet";
export type StopState = "done" | "now" | "next";
export interface TrackerStop {
  label: MessageKey;
  state: StopState;
  /** What the date under the label means: when it happened, when Stripe releases it, or the latest it lands. */
  when: { kind: "on" | "ready" | "by"; date: string } | null;
}

export const isMoving = (p: Pick<PayoutBatch, "payoutStatus">): boolean =>
  MOVING.has(String(p.payoutStatus || "").toLowerCase());

/** The cash-out to track: the newest one still moving (the list is newest first). */
export const movingPayout = (rows: PayoutBatch[]): PayoutBatch | null => rows.find(isMoving) ?? null;

export function trackerKind(p: PayoutBatch): TrackerKind {
  if (p.payoutProvider === "masspay" || p.destination?.kind === "wallet") return "wallet";
  if (p.payoutMethod === "instant" || p.destination?.kind === "card") return "card";
  return "bank";
}

/** "Tue, Oct 20". Release and bank dates are whole days stamped in UTC; the cash-out itself is the talent's own day. */
export function trackerDate(iso: string, { utc = true }: { utc?: boolean } = {}): string {
  return new Date(iso).toLocaleDateString(formatLocale(), {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(utc ? { timeZone: "UTC" } : {}),
  });
}

/** "Nov 2" — the Payout History row, where a weekday doesn't fit. */
export function trackerShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(formatLocale(), { month: "short", day: "numeric", timeZone: "UTC" });
}

export function trackerStops(p: PayoutBatch): TrackerStop[] {
  const status = String(p.payoutStatus || "").toLowerCase();
  const cashedOut: TrackerStop = { label: "payoutTracker.cashedOut", state: "done", when: { kind: "on", date: trackerDate(p.createdAt, { utc: false }) } };
  const by = p.arrivalAt ? { kind: "by" as const, date: trackerDate(p.arrivalAt) } : null;
  const kind = trackerKind(p);
  if (kind !== "bank") {
    return [cashedOut, { label: kind === "card" ? "payoutTracker.onYourCard" : "payoutTracker.inYourWallet", state: "now", when: by }];
  }
  const clearing = status === "awaiting_funds";
  const release = p.releaseAt ? trackerDate(p.releaseAt) : null;
  return [
    cashedOut,
    { label: "payoutTracker.clearing", state: clearing ? "now" : "done", when: release ? { kind: "ready", date: release } : null },
    { label: "payoutTracker.sentToYourBank", state: clearing ? "next" : "done", when: release ? { kind: "on", date: release } : null },
    { label: "payoutTracker.inYourBank", state: clearing ? "next" : "now", when: by },
  ];
}


/**
 * How full each leg of the bar is (0–1), one entry per gap between stops.
 * The leg in progress fills by the time that has passed on it when both ends
 * have a date; otherwise it shows a sliver, so "moving" never reads as "stuck".
 */
export function trackerLegs(p: PayoutBatch, now: Date = new Date()): number[] {
  const stops = trackerStops(p);
  const start = new Date(p.createdAt).getTime();
  const release = p.releaseAt ? new Date(p.releaseAt).getTime() : null;
  const arrival = p.arrivalAt ? new Date(p.arrivalAt).getTime() : null;
  const frac = (a: number | null, b: number | null) =>
    a != null && b != null && b > a ? Math.min(0.92, Math.max(0.08, (now.getTime() - a) / (b - a))) : 0.15;
  const ends: Array<[number | null, number | null]> =
    stops.length === 2 ? [[start, arrival]] : [[start, release], [release, release], [release, arrival]];
  return ends.map(([a, b], i) => {
    const next = stops[i + 1].state;
    if (next === "done") return 1;
    if (next === "now") return frac(a, b);
    return 0;
  });
}

/** "~13 days" up to 10 days, then "~2 weeks": the key and count for t(). */
export function aboutDuration(days: number): { key: MessageKey; count: number } {
  return days > 10
    ? { key: "payoutTracker.aboutWeeks", count: Math.round(days / 7) }
    : { key: "payoutTracker.aboutDays", count: Math.max(1, Math.round(days)) };
}

/** True while it's worth a sliver of motion: the bar has a leg in progress. */
export const hasLiveLeg = (legs: number[]): boolean => legs.some((f) => f > 0 && f < 1);

